(() => {
  const ROOT_SELECTOR = '[data-ch-wholesale-locator]';
  const GOOGLE_CALLBACK = 'CHWholesaleLocatorGoogleReady';
  const GOOGLE_PROMISE_KEY = '__CHWholesaleLocatorGooglePromise';

  const escapeHTML = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = String(url).trim();
    if (!trimmed) return '';
    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  };

  const isCampHollowUrl = (url) => {
    if (!url) return false;

    try {
      const parsed = new URL(normalizeUrl(url));
      const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
      return host === 'camphollow.com' || host === 'camp-hollow.com' || host === 'camphollow.myshopify.com' || host === 'camp-hollow.myshopify.com';
    } catch (_error) {
      return String(url).toLowerCase().includes('camphollow') || String(url).toLowerCase().includes('camp-hollow');
    }
  };

  const compact = (values) => values.map((value) => String(value || '').trim()).filter(Boolean);

  const formatAddress = (store) => compact([
    store.addressLine1,
    store.addressLine2,
    compact([store.city, store.state, store.postalCode]).join(', '),
    store.country,
  ]).join('<br>');

  const plainAddress = (store) => compact([
    store.addressLine1,
    store.addressLine2,
    compact([store.city, store.state, store.postalCode]).join(', '),
    store.country,
  ]).join(', ');

  const getImageUrl = (store) => store.image?.url || store.image?.source || store.image || '';

  const optimizedImageUrl = (url, width = 180) => {
    if (!url) return '';

    try {
      const imageUrl = new URL(url, window.location.origin);
      const isShopifyImage = imageUrl.hostname.includes('shopify') || imageUrl.pathname.includes('/cdn/shop/');

      if (isShopifyImage) {
        imageUrl.searchParams.set('width', String(width));
      }

      return imageUrl.toString();
    } catch (_error) {
      return url;
    }
  };

  const distanceKm = (origin, destination) => {
    const radius = 6371;
    const toRad = (degrees) => degrees * Math.PI / 180;
    const dLat = toRad(destination.lat - origin.lat);
    const dLng = toRad(destination.lng - origin.lng);
    const lat1 = toRad(origin.lat);
    const lat2 = toRad(destination.lat);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  function loadGoogleMaps(apiKey) {
    if (window.google?.maps) return Promise.resolve(window.google.maps);
    if (window[GOOGLE_PROMISE_KEY]) return window[GOOGLE_PROMISE_KEY];

    window[GOOGLE_PROMISE_KEY] = new Promise((resolve, reject) => {
      const fail = (error) => {
        window.clearTimeout(timeout);
        window[GOOGLE_CALLBACK] = () => {};
        reject(error);
      };
      const timeout = window.setTimeout(() => fail(new Error('Google Maps timed out')), 15000);
      window[GOOGLE_CALLBACK] = () => {
        window.clearTimeout(timeout);
        if (!window.google?.maps) {
          fail(new Error('Google Maps is unavailable'));
          return;
        }
        resolve(window.google.maps);
      };

      const script = document.createElement('script');
      const params = new URLSearchParams({ key: apiKey, callback: GOOGLE_CALLBACK, libraries: 'marker', loading: 'async' });
      script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => fail(new Error('Google Maps failed to load'));
      document.head.append(script);
    });

    return window[GOOGLE_PROMISE_KEY];
  }

  class WholesaleLocator {
    constructor(root) {
      this.root = root;
      this.mapElement = root.querySelector('[data-locator-map]');
      this.listElement = root.querySelector('[data-locator-list]');
      this.statusElement = root.querySelector('[data-locator-status]');
      this.form = root.querySelector('[data-locator-search-form]');
      this.input = root.querySelector('[data-locator-search-input]');
      this.resetButton = root.querySelector('[data-locator-reset]');

      this.proxyBase = (root.dataset.proxyBase || '/apps/wholesale-locator').replace(/\/$/, '');
      this.shopDomain = root.dataset.shopDomain || '';
      this.apiKey = root.dataset.googleApiKey || '';
      this.mapId = root.dataset.googleMapId || '';
      this.markerPin = root.dataset.markerPin || '';
      this.defaultCenter = {
        lat: toNumber(root.dataset.defaultLat, 39.8283),
        lng: toNumber(root.dataset.defaultLng, -98.5795),
      };
      this.defaultZoom = toNumber(root.dataset.defaultZoom, 4);
      this.searchZoom = toNumber(root.dataset.searchZoom, 10);
      this.maxDistanceKm = toNumber(root.dataset.maxDistanceKm, 80);
      this.messages = {
        loading: root.dataset.loadingText || 'Loading verified stores...',
        empty: root.dataset.emptyText || 'No verified stores are available yet.',
        error: root.dataset.errorText || 'We could not load the store locator. Please try again in a moment.',
        noResults: root.dataset.noResultsText || 'No stores were found within this radius. Try a different city or ZIP code.',
        geocodeError: root.dataset.geocodeErrorText || 'We could not find that location. Try a city, state, or ZIP code.',
      };

      this.map = null;
      this.geocoder = null;
      this.infoWindow = null;
      this.mapReady = false;
      this.markers = new Map();
      this.stores = [];
      this.storesLoaded = false;
      this.visibleStores = [];
      this.listStores = [];
      this.activeId = null;
    }

    async init() {
      this.bindEvents();
      this.setStatus(this.messages.loading);

      // Neither task waits for the other: the store list works even without Maps.
      const mapTask = this.loadMap();
      try {
        await this.fetchStores();
        this.storesLoaded = true;
        this.visibleStores = [...this.stores];
        this.listStores = [...this.visibleStores];
        this.render();
      } catch (error) {
        console.error(error);
        this.setStatus(this.messages.error);
      }
      await mapTask;
    }

    async loadMap() {
      if (!this.apiKey) {
        this.showMapUnavailable('Map is temporarily unavailable.');
        return;
      }

      try {
        await loadGoogleMaps(this.apiKey);
        await this.initializeMap();
        this.mapReady = true;
        await this.renderMarkers();
        this.updateListFromMap();
      } catch (error) {
        console.error(error);
        this.showMapUnavailable('Map is temporarily unavailable.');
      }
    }

    bindEvents() {
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.search(this.input?.value || '');
      });

      this.resetButton?.addEventListener('click', () => this.resetSearch());
    }

    async initializeMap() {
      const mapOptions = {
        center: this.defaultCenter,
        zoom: this.defaultZoom,
        mapId: this.mapId || undefined,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: true,
      };

      this.map = new google.maps.Map(this.mapElement, mapOptions);
      this.geocoder = new google.maps.Geocoder();
      this.infoWindow = new google.maps.InfoWindow({ maxWidth: 320 });
      this.infoWindow.addListener('closeclick', () => this.restoreListSelection());
      this.map.addListener('idle', () => this.updateListFromMap());
    }

    async fetchStores() {
      const response = await fetch(this.proxyUrl('/locations'), {
        headers: { Accept: 'application/json' },
        signal: AbortSignal.timeout(60000),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || data.ok === false) throw new Error(data.error || 'Unable to load locations');

      const rawStores = Array.isArray(data.locations) ? data.locations : Array.isArray(data.wholesalers) ? data.wholesalers : [];
      this.stores = rawStores
        .map((store) => ({
          ...store,
          latitude: toNumber(store.latitude, NaN),
          longitude: toNumber(store.longitude, NaN),
        }))
        .filter((store) => Number.isFinite(store.latitude) && Number.isFinite(store.longitude));
    }

    render() {
      this.updateListFromMap(true);
      this.renderMarkers();
    }

    updateListFromMap(force = false) {
      if (!this.storesLoaded) return;
      const bounds = this.mapReady ? this.map?.getBounds() : null;
      const stores = bounds
        ? this.visibleStores.filter((store) => bounds.contains({ lat: store.latitude, lng: store.longitude }))
        : [...this.visibleStores];
      const changed = stores.length !== this.listStores.length
        || stores.some((store, index) => String(store.id) !== String(this.listStores[index]?.id));
      this.listStores = stores;
      if (this.activeId && !stores.some((store) => String(store.id) === this.activeId)) {
        this.activeId = null;
        this.infoWindow?.close();
        this.root.querySelectorAll('[data-marker-id]').forEach((marker) => marker.classList.remove('is-active'));
      }
      if (force || changed) {
        this.renderList();
        if (this.listElement) this.listElement.scrollTop = 0;
      }

      if (!this.stores.length) {
        this.setStatus(this.messages.empty);
        return;
      }
      const count = stores.length;
      const total = this.stores.length;
      const summary = `${count} of ${total} ${total === 1 ? 'store' : 'stores'} shown.`;
      if (!this.visibleStores.length) {
        this.setStatus(`${summary} ${this.messages.noResults}`);
      } else if (bounds && !count) {
        this.setStatus(`${summary} Move the map or zoom out to see more stores.`);
      } else {
        this.setStatus(summary);
      }
    }

    renderList() {
      if (!this.listElement) return;

      const stores = this.listStores;
      this.listElement.innerHTML = stores.map((store) => this.storeCardTemplate(store)).join('');

      this.listElement.querySelectorAll('[data-store-id]').forEach((button) => {
        button.addEventListener('click', () => this.selectStore(button.dataset.storeId, true));
      });
    }

    storeCardTemplate(store) {
      const imageUrl = getImageUrl(store);
      const url = normalizeUrl(store.website);
      const stocks = Array.isArray(store.stocks) ? store.stocks : [];
      const distance = typeof store.distanceKm === 'number' ? `<p class="ch-wholesale-locator__card-distance">${store.distanceKm.toFixed(1)} km away</p>` : '';
      const optimizedImage = optimizedImageUrl(imageUrl, 180);
      const image = optimizedImage ? `
        <img
          class="ch-wholesale-locator__card-image"
          src="${escapeHTML(optimizedImage)}"
          srcset="${escapeHTML(optimizedImageUrl(imageUrl, 96))} 96w, ${escapeHTML(optimizedImageUrl(imageUrl, 180))} 180w, ${escapeHTML(optimizedImageUrl(imageUrl, 320))} 320w"
          sizes="(max-width: 640px) calc(100vw - 2rem), 96px"
          alt="${escapeHTML(store.name)}"
          width="96"
          height="96"
          loading="lazy"
          decoding="async">
      ` : '';
      const contact = compact([
        store.email ? `<a href="mailto:${escapeHTML(store.email)}">Email</a>` : '',
        url && !isCampHollowUrl(url) ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer">Website</a>` : '',
      ]).join(' · ');

      return `
        <li class="ch-wholesale-locator__card">
          <button class="ch-wholesale-locator__card-button" type="button" data-store-id="${escapeHTML(store.id)}" aria-current="${String(store.id) === String(this.activeId) ? 'true' : 'false'}">
            <span>
              <span class="ch-wholesale-locator__card-title">${escapeHTML(store.name)}</span>
              <span class="ch-wholesale-locator__card-address">${formatAddress(store)}</span>
              ${contact ? `<span class="ch-wholesale-locator__card-contact">${contact}</span>` : ''}
              ${distance}
              ${stocks.length ? `<span class="ch-wholesale-locator__chips">${stocks.map((stock) => `<span class="ch-wholesale-locator__chip">${escapeHTML(stock)}</span>`).join('')}</span>` : ''}
            </span>
            ${image}
          </button>
        </li>
      `;
    }

    async renderMarkers() {
      if (!this.mapReady || !this.map) return;
      const renderId = this.markerRenderId = (this.markerRenderId || 0) + 1;

      this.markers.forEach((marker) => {
        marker.setMap?.(null);
        if ('map' in marker) marker.map = null;
      });
      this.markers.clear();

      let AdvancedMarkerElement = null;
      if (google.maps.importLibrary) {
        try {
          ({ AdvancedMarkerElement } = await google.maps.importLibrary('marker'));
        } catch (_error) {}
      }

      // Maps and locations can finish together while the marker library is loading.
      if (renderId !== this.markerRenderId) return;

      for (const store of this.visibleStores) {
        const position = { lat: store.latitude, lng: store.longitude };
        let marker;

        if (AdvancedMarkerElement && this.mapId) {
          const content = this.createMarkerContent(store);
          marker = new AdvancedMarkerElement({ map: this.map, position, title: store.name, content });
          marker.addEventListener?.('gmp-click', () => this.selectStore(store.id, false));
          if (!marker.addEventListener) marker.addListener('click', () => this.selectStore(store.id, false));
          content.addEventListener('click', () => this.selectStore(store.id, false));
        } else {
          marker = new google.maps.Marker({
            map: this.map,
            position,
            title: store.name,
            icon: this.markerPin ? { url: this.markerPin, scaledSize: new google.maps.Size(42, 42) } : undefined,
          });
          marker.addListener('click', () => this.selectStore(store.id, false));
        }

        this.markers.set(String(store.id), marker);
      }
    }

    createMarkerContent(store) {
      const marker = document.createElement('button');
      marker.type = 'button';
      marker.className = 'ch-wholesale-locator__pin';
      marker.setAttribute('aria-label', `View ${store.name}`);
      marker.dataset.markerId = store.id;

      if (this.markerPin) {
        marker.innerHTML = `<img src="${escapeHTML(this.markerPin)}" alt="" aria-hidden="true">`;
      }

      return marker;
    }

    selectStore(id, shouldPan = true) {
      const store = this.visibleStores.find((item) => String(item.id) === String(id));
      if (!store) return;

      this.activeId = String(id);
      const isMobileLayout = this.isMobileLayout();

      this.root.querySelectorAll('[data-store-id]').forEach((button) => {
        button.setAttribute('aria-current', String(button.dataset.storeId) === this.activeId ? 'true' : 'false');
      });
      this.root.querySelectorAll('[data-marker-id]').forEach((marker) => {
        marker.classList.toggle('is-active', String(marker.dataset.markerId) === this.activeId);
      });

      const marker = this.markers.get(this.activeId);
      const position = { lat: store.latitude, lng: store.longitude };
      if (!this.mapReady || !this.map || !this.infoWindow) return;

      if (isMobileLayout) {
        this.infoWindow.close();
        this.map.panTo(position);
        this.map.setZoom(Math.max(this.map.getZoom() || this.defaultZoom, this.searchZoom));
        return;
      }

      this.infoWindow.setContent(this.infoTemplate(store));
      this.infoWindow.open({ map: this.map, anchor: marker });
      window.setTimeout(() => this.styleInfoWindow(), 0);

      if (shouldPan) {
        this.map.panTo(position);
        this.map.setZoom(Math.max(this.map.getZoom() || this.defaultZoom, this.searchZoom));
      }
    }

    infoTemplate(store) {
      const url = normalizeUrl(store.website);
      const stocks = Array.isArray(store.stocks) ? store.stocks : [];
      const contact = compact([
        store.email ? `<a href="mailto:${escapeHTML(store.email)}">Email</a>` : '',
        url && !isCampHollowUrl(url) ? `<a href="${escapeHTML(url)}" target="_blank" rel="noopener noreferrer">Website</a>` : '',
      ]).join(' · ');

      return `
        <div class="ch-wholesale-locator__info">
          <div class="ch-wholesale-locator__info-header">
            <div class="ch-wholesale-locator__info-copy">
              <h3>${escapeHTML(store.name)}</h3>
            </div>
            <button class="ch-wholesale-locator__info-close" type="button" aria-label="Close" data-info-close></button>
          </div>
          <div class="ch-wholesale-locator__info-body">
            <p>${formatAddress(store)}</p>
            ${contact ? `<p>${contact}</p>` : ''}
            ${stocks.length ? `<p><strong>Stocks:</strong> ${stocks.map(escapeHTML).join(', ')}</p>` : ''}
          </div>
        </div>
      `;
    }

    styleInfoWindow() {
      this.mapElement?.querySelectorAll('.gm-ui-hover-effect').forEach((button) => {
        button.classList.add('ch-wholesale-locator__google-close');
        button.setAttribute('aria-label', 'Close');
      });

      this.mapElement?.querySelectorAll('[data-info-close]').forEach((button) => {
        button.addEventListener('click', () => {
          this.infoWindow?.close();
          this.restoreListSelection();
        }, { once: true });
      });
    }

    restoreListSelection() {
      this.activeId = null;
      this.updateListFromMap(true);
      this.root.querySelectorAll('[data-marker-id]').forEach((marker) => marker.classList.remove('is-active'));
    }

    async search(query) {
      const searchTerm = query.trim();
      if (!searchTerm) {
        this.resetSearch();
        return;
      }

      this.setStatus('Searching...');

      try {
        if (!this.geocoder) {
          const normalized = searchTerm.toLowerCase();
          this.visibleStores = this.stores.filter((store) => compact([
            store.name,
            store.addressLine1,
            store.addressLine2,
            store.city,
            store.state,
            store.postalCode,
            store.country,
          ]).join(' ').toLowerCase().includes(normalized));

          this.listStores = [...this.visibleStores];
          this.resetButton.hidden = false;
          this.activeId = null;
          this.render();
          return;
        }

        const result = await this.geocode(searchTerm);
        this.visibleStores = this.stores
          .map((store) => ({ ...store, distanceKm: distanceKm(result, { lat: store.latitude, lng: store.longitude }) }))
          .filter((store) => store.distanceKm <= this.maxDistanceKm)
          .sort((a, b) => a.distanceKm - b.distanceKm);

        this.listStores = [...this.visibleStores];
        this.resetButton.hidden = false;
        this.activeId = null;
        this.render();
        this.map.setCenter(result);
        this.map.setZoom(this.searchZoom);
        this.fitToVisibleStores(result);
      } catch (error) {
        console.error(error);
        this.setStatus(this.messages.geocodeError);
      }
    }

    geocode(address) {
      return new Promise((resolve, reject) => {
        this.geocoder.geocode({ address }, (results, status) => {
          if (status !== 'OK' || !results?.[0]) {
            reject(new Error(status));
            return;
          }

          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        });
      });
    }

    resetSearch() {
      if (this.input) this.input.value = '';
      if (this.resetButton) this.resetButton.hidden = true;
      this.visibleStores = [...this.stores];
      this.listStores = [...this.visibleStores];
      this.activeId = null;
      this.render();
      if (this.mapReady && this.map) {
        this.map.setCenter(this.defaultCenter);
        this.map.setZoom(this.defaultZoom);
        this.fitToVisibleStores();
      }
    }

    fitToVisibleStores(extraPoint) {
      window.clearTimeout(this.fitZoomTimer);
      if (!this.mapReady || !this.map || !this.visibleStores.length) return;

      const bounds = new google.maps.LatLngBounds();
      this.visibleStores.forEach((store) => bounds.extend({ lat: store.latitude, lng: store.longitude }));
      if (extraPoint) bounds.extend(extraPoint);
      this.map.fitBounds(bounds, 72);

      if (this.visibleStores.length === 1) {
        this.fitZoomTimer = window.setTimeout(() => this.map.setZoom(Math.min(this.searchZoom, 12)), 150);
      }
    }

    setStatus(message) {
      if (this.statusElement) this.statusElement.textContent = message;
    }

    isMobileLayout() {
      return window.matchMedia('(max-width: 989px)').matches;
    }

    proxyUrl(path) {
      const url = new URL(`${this.proxyBase}${path}`, window.location.origin);
      const isLocalPreview = ['127.0.0.1', 'localhost'].includes(window.location.hostname);
      if (isLocalPreview && this.shopDomain && !url.searchParams.has('shop')) {
        url.searchParams.set('shop', this.shopDomain);
      }
      return url.toString();
    }

    showMapUnavailable(message) {
      if (!this.mapElement) return;
      this.mapElement.classList.add('is-unavailable');
      this.mapElement.innerHTML = `<p>${escapeHTML(message)}</p>`;
    }
  }

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.chWholesaleLocatorReady === 'true') return;
      root.dataset.chWholesaleLocatorReady = 'true';
      new WholesaleLocator(root).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
